'use client'

import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { MOBILE_MINT, IOT_MINT, HNT_MINT } from '@helium/spl-utils'

/** Flat shape returned by /api/treasury/hotspots. */
export type HotspotSummary = {
  id: string
  name: string
  image: string
  pendingRewards: Record<string, string>
}

const zeroRewards = (): Record<string, string> => ({
  [`${MOBILE_MINT.toBase58()}`]: '0',
  [`${IOT_MINT.toBase58()}`]: '0',
  [`${HNT_MINT.toBase58()}`]: '0',
})

type TreasuryHotspotsResponse = {
  hotspots: HotspotSummary[]
  totalPendingRewards: Record<string, string>
  updatedAt: string
}

const useHotspotsHook = () => {
  // undefined while loading so consumers keep showing their skeletons.
  const [hotspots, setHotspots] = useState<HotspotSummary[] | undefined>(
    undefined,
  )
  const [totalPendingRewards, setTotalPendingRewards] =
    useState<Record<string, string>>(zeroRewards())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchHotspots = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/treasury/hotspots')
      const data = (await res.json()) as TreasuryHotspotsResponse & {
        error?: string
      }

      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`)

      setHotspots(data.hotspots || [])
      setTotalPendingRewards({
        ...zeroRewards(),
        ...(data.totalPendingRewards || {}),
      })
    } catch (e) {
      console.error('Failed to fetch hotspots:', e)
      setError(e instanceof Error ? e.message : 'Failed to fetch hotspots')
      // Empty rather than undefined: a failure should end the skeleton, not
      // leave it spinning forever.
      setHotspots([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchHotspots()
  }, [fetchHotspots])

  return {
    hotspots,
    totalPendingRewards,
    loading,
    error,
    refetch: fetchHotspots,
  }
}

export type HotspotsContextManager = ReturnType<typeof useHotspotsHook>

const HotspotsContext = createContext<HotspotsContextManager | null>(null)

const { Provider } = HotspotsContext

const HotspotsProvider = ({ children }: { children: ReactNode }) => {
  return <Provider value={useHotspotsHook()}>{children}</Provider>
}

const useHotspots = (): HotspotsContextManager => {
  const context = useContext(HotspotsContext)
  if (!context) {
    throw new Error('useHotspots has to be used within <HotspotsProvider>')
  }
  return context
}
export { useHotspots, HotspotsProvider }
